import { FormEvent, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { forgotPassword, resetPassword } from '../services/auth';
import { IconMark, IconMail } from '../components/Icons';

export default function ForgotPassword() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetStep, setResetStep] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await forgotPassword(email);
      setResetStep(true);
      setResendCooldown(60);
    } catch {
      setError(t('auth:forgotPassword.requestFailed', 'Something went wrong. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      await resetPassword(email, code, password);
      navigate('/login', { replace: true });
    } catch {
      setError(t('auth:forgotPassword.invalidCode', 'Invalid or expired code.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await forgotPassword(email);
      setResendCooldown(60);
    } catch {
      setError(t('auth:forgotPassword.requestFailed', 'Something went wrong. Please try again.'));
    }
  };

  const Side = (
    <div
      style={{
        padding: isMobile ? '28px 20px' : '40px 56px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: isMobile ? 20 : 0,
        background: 'var(--bg-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Link to="/login" className="nav-link">
          {t('auth:forgotPassword.backToLogin', 'Back to sign in →')}
        </Link>
      </div>

      <div>
        <div className="eyebrow" style={{ marginBottom: 14, color: 'var(--accent)' }}>
          {t('auth:forgotPassword.eyebrowQuote', 'Account recovery')}
        </div>
        <blockquote
          style={{
            margin: 0,
            padding: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 30,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            color: 'var(--text)',
          }}
        >
          {t('auth:forgotPassword.testimonial', '“Back to building in under a minute.”')}
        </blockquote>
      </div>

      <div />
    </div>
  );

  return (
    <div style={{ height: isMobile ? 'auto' : '100dvh', minHeight: '100dvh', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', background: 'var(--bg)' }}>
      <div
        style={{
          padding: isMobile ? '28px 20px' : '40px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: isMobile ? 28 : 0,
          borderRight: isMobile ? 'none' : '1px solid var(--line)',
        }}
      >
        <Link to="/" className="brand" style={{ fontSize: 18 }}>
          <IconMark size={22} />
          {t('common:app.name', 'Entire')}
        </Link>

        {resetStep ? (
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              {t('auth:forgotPassword.reset.eyebrow', 'Reset password')}
            </div>
            <h2 className="display tight" style={{ fontSize: 40, lineHeight: 1.05, margin: '0 0 14px' }}>
              {t('auth:forgotPassword.reset.title', 'Check your inbox.')}
            </h2>
            <p className="muted-2" style={{ fontSize: 15, margin: '0 0 12px', maxWidth: 420, lineHeight: 1.5, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <IconMail />
              {t('auth:forgotPassword.reset.subtitle', { email, defaultValue: `If an account exists for ${email}, we've sent a 6-digit code.` })}
            </p>

            <div
              style={{
                padding: '10px 14px',
                border: '1px solid rgba(255,184,0,0.28)',
                background: 'rgba(255,184,0,0.07)',
                color: 'var(--text-2)',
                borderRadius: 10,
                fontSize: 13,
                maxWidth: 420,
                margin: '0 0 24px',
              }}
            >
              {t('auth:forgotPassword.reset.spamHint', "Don't see the email? Please check your spam or junk folder — it may take a minute to arrive.")}
            </div>

            <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 380 }}>
              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    border: '1px solid var(--danger-line)',
                    background: 'var(--danger-wash)',
                    color: 'var(--danger)',
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              <div className="field">
                <label htmlFor="code">{t('auth:forgotPassword.reset.codeLabel', 'Verification code')}</label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  className="input mono"
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.4em' }}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>

              <div className="field">
                <label htmlFor="new-password">{t('auth:forgotPassword.reset.passwordLabel', 'New password')}</label>
                <input
                  id="new-password"
                  type="password"
                  required
                  minLength={6}
                  className="input"
                  placeholder={t('auth:forgotPassword.reset.passwordPlaceholder', 'New password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button
                className="btn primary lg"
                type="submit"
                disabled={isSubmitting || code.length !== 6 || password.length < 6}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                {isSubmitting
                  ? t('auth:forgotPassword.reset.resetting', 'Resetting…')
                  : t('auth:forgotPassword.reset.resetButton', 'Reset password')}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <button
                  type="button"
                  className="nav-link"
                  style={{ height: 'auto', padding: 0, color: 'var(--text-5)', fontSize: 12 }}
                  onClick={() => {
                    setResetStep(false);
                    setCode('');
                    setPassword('');
                    setError('');
                  }}
                >
                  {t('auth:forgotPassword.reset.back', '← Back')}
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="nav-link"
                  style={{ height: 'auto', padding: 0, color: resendCooldown > 0 ? 'var(--text-6)' : 'var(--accent)', fontSize: 12 }}
                >
                  {resendCooldown > 0
                    ? t('auth:forgotPassword.reset.resendCooldown', { seconds: resendCooldown, defaultValue: `Resend in ${resendCooldown}s` })
                    : t('auth:forgotPassword.reset.resendButton', 'Resend code')}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <div className="eyebrow" style={{ marginBottom: 18 }}>
              {t('auth:forgotPassword.request.eyebrow', 'Forgot password')}
            </div>
            <h2 className="display tight" style={{ fontSize: 40, lineHeight: 1.05, margin: '0 0 14px' }}>
              {t('auth:forgotPassword.request.title', 'Reset your password.')}
            </h2>
            <p className="muted-2" style={{ fontSize: 15, margin: '0 0 32px', maxWidth: 380, lineHeight: 1.5 }}>
              {t('auth:forgotPassword.request.subtitle', "Enter your email and we'll send you a code to reset it.")}
            </p>

            <form onSubmit={handleRequest} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    border: '1px solid var(--danger-line)',
                    background: 'var(--danger-wash)',
                    color: 'var(--danger)',
                    borderRadius: 10,
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  {error}
                </div>
              )}

              <div className="field">
                <input
                  id="email"
                  type="email"
                  required
                  className="input"
                  placeholder={t('auth:forgotPassword.request.emailPlaceholder', 'you@studio.com')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button className="btn primary lg" type="submit" disabled={isSubmitting} style={{ width: '100%', justifyContent: 'center' }}>
                {isSubmitting
                  ? t('auth:forgotPassword.request.sending', 'Sending…')
                  : t('auth:forgotPassword.request.sendButton', 'Send reset code')}
              </button>

              <Link
                to="/login"
                className="nav-link"
                style={{ height: 'auto', padding: 0, alignSelf: 'flex-start', color: 'var(--text-5)', fontSize: 12, marginTop: 4 }}
              >
                {t('auth:forgotPassword.backToLogin', 'Back to sign in →')}
              </Link>
            </form>
          </div>
        )}

        <div />
      </div>

      {Side}
    </div>
  );
}
